import React, { ReactElement, ReactNode, useContext, useEffect } from 'react'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import Fieldset from '../../Common/Components/Fieldset'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import FormRow from '../../Common/Components/FormRow'
import Checkbox from '../../Common/Components/Checkbox'
import { ACDCodesLanguage } from '../../../../i18n'
import Button from '../../Common/Components/Button'
import { NOT_DEFINED_VALUE_STRING } from '../../Common/Constants/DefaultValues'
import TwoColumnSelectOption from '../../Common/Components/TwoColumnSelectOption'
import { ACDNACodeValue } from '../Types'
import { GenericFilterEntityDataItems, RptFilterEntityInfo } from '../../Common/Types/Types'
import { HeadingLevel } from '../../Common/Components/Heading/Heading' // import './acdCodesTab.scss'

// import './acdCodesTab.scss'

export type Props = {
    statisticSettingsACDNACodes: ACDNACodeValue[]
    setStatisticSettingsACDNACodes: (codes: ACDNACodeValue[]) => void
    useCodeDescriptionForACDNACodeStatisticSettings: boolean
    setUseCodeDescriptionForACDNACodeStatisticSettings: (checked: boolean) => void
    rptFilterEntityInfo: RptFilterEntityInfo | undefined
    reportLoaded: boolean
}

/**
 * Report Modal - ACD Codes Tab component.
 * @param statisticSettingsACDNACodes  Report property configuring ACDNACodes statistics settings.
 * @param setStatisticSettingsACDNACodes Setter for the property with identical name.
 * @param useCodeDescriptionForACDNACodeStatisticSettings  Report property.
 * @param setUseCodeDescriptionForACDNACodeStatisticSettings Setter for the property with identical name.
 * @param rptFilterEntityInfo Report Filter Entity Info response property - should contain available ACD N/A codes for current telephony system.
 */
const ACDCodesTab: React.FC<Props> = ({
    statisticSettingsACDNACodes,
    setStatisticSettingsACDNACodes,
    useCodeDescriptionForACDNACodeStatisticSettings,
    setUseCodeDescriptionForACDNACodeStatisticSettings,
    rptFilterEntityInfo,
    reportLoaded
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ACDCodesLanguage')

    const updateStatisticSettingsACDNACode = (codeIdentifier: number, newCode: string): void => {
        const newStatisticSettingsACDNACodes = statisticSettingsACDNACodes.map((value) => {
            // If any existing value not with currently change codeIdentifier have the same value, change it to NOT_DEFINED.
            if (value.codeIdentifier !== codeIdentifier && value.codeValue === newCode) {
                // Set to undefined if the same value is being set on different position.
                return {
                    codeIdentifier: value.codeIdentifier,
                    codeValue: NOT_DEFINED_VALUE_STRING
                }
            }
            // Set the new value for specified codeIdentifier.
            if (value.codeIdentifier === codeIdentifier) {
                return {
                    codeIdentifier,
                    codeValue: newCode
                }
            }
            // Return value without change if change is not needed.
            return value
        })

        setStatisticSettingsACDNACodes(newStatisticSettingsACDNACodes)
    }

    const resetStatisticSettingsACDNACodes = (): void => {
        const newStatisticSettingsACDNACodes: ACDNACodeValue[] = []
        const entityInfoACDNAData: Definable<GenericFilterEntityDataItems[]> =
            rptFilterEntityInfo?.EntityInfo.CodesACDNotAvailable?.Data

        for (let i = 1; i <= 11; i++) {
            if (entityInfoACDNAData && entityInfoACDNAData[i]) {
                // We index -1 for each item to account for zero indexing arrays.
                // Otherwise the array starts with an undefined item at the 0 index.
                newStatisticSettingsACDNACodes[i - 1] = { codeIdentifier: i, codeValue: entityInfoACDNAData[i].ID }
            } else {
                // If there is no code for this codeIdentifier (there were less than 10 codes provided in report entity info)
                // fill in the remaining ACDNA Codes with not defined value.
                newStatisticSettingsACDNACodes.push({ codeIdentifier: i, codeValue: NOT_DEFINED_VALUE_STRING })
            }
        }
        setStatisticSettingsACDNACodes(newStatisticSettingsACDNACodes)
    }

    const ACDNACodesAsOptions: SelectOptions<string> =
        rptFilterEntityInfo && rptFilterEntityInfo.EntityInfo.CodesACDNotAvailable
            ? rptFilterEntityInfo.EntityInfo.CodesACDNotAvailable.Data.map((genericFilterEntityItem) => ({
                  value: genericFilterEntityItem.ID,
                  label: genericFilterEntityItem.Description,
                  renderOptionElement: (
                      <TwoColumnSelectOption
                          label={genericFilterEntityItem.Description}
                          value={genericFilterEntityItem.ID}
                      />
                  )
              }))
            : []

    const toCodeSelect = (label: string, codeIdentifier: number): ReactNode => {
        const statisticSettingValue = statisticSettingsACDNACodes.filter(
            (value) => value.codeIdentifier === codeIdentifier
        )[0]
        return (
            <FormRow isInline className={`acd-na-code-${codeIdentifier}`}>
                <Select<string>
                    label={label}
                    options={ACDNACodesAsOptions}
                    value={statisticSettingValue ? statisticSettingValue.codeValue : ''}
                    handleChange={(newCode: string): void => updateStatisticSettingsACDNACode(codeIdentifier, newCode)}
                    renderSelectedValue={true}
                />
            </FormRow>
        )
    }

    useEffect((): void => {
        if (!reportLoaded) return
        resetStatisticSettingsACDNACodes()
    }, [JSON.stringify(rptFilterEntityInfo?.EntityInfo?.CodesACDNotAvailable)])

    return (
        <div className='acd-codes-tab tab-area'>
            <Fieldset className={'acd-codes'}>
                <Heading level={HeadingLevel.ONE}>
                    {language?.get(ACDCodesLanguage.ACDCodesLanguage_SectionHeader)}
                </Heading>

                <P>{language?.get(ACDCodesLanguage.SectionHint)}</P>

                {statisticSettingsACDNACodes.length > 0 && ACDNACodesAsOptions && (
                    <div>
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode01Label), 1)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode02Label), 2)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode03Label), 3)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode04Label), 4)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode05Label), 5)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode06Label), 6)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode07Label), 7)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode08Label), 8)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode09Label), 9)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode10Label), 10)}
                        {toCodeSelect(language?.get(ACDCodesLanguage.NaCode11Label), 11)}
                    </div>
                )}
            </Fieldset>

            <Fieldset className='code-desc'>
                <Checkbox
                    onChange={setUseCodeDescriptionForACDNACodeStatisticSettings}
                    isChecked={useCodeDescriptionForACDNACodeStatisticSettings}
                    labelLeft={language.get(ACDCodesLanguage.UseCodeDescriptionLabel)}
                />
            </Fieldset>

            <Fieldset className={'reset-codes'}>
                <Heading level={HeadingLevel.TWO}>{language?.get(ACDCodesLanguage.ResetCodesHeader)}</Heading>
                <P>{language.get(ACDCodesLanguage.ResetCodesHint)}</P>
                <Button
                    label={language.get(ACDCodesLanguage.ResetCodesButtonText)}
                    onClick={resetStatisticSettingsACDNACodes}
                    isOutlined={false}
                />
            </Fieldset>
        </div>
    )
}

export default ACDCodesTab
